// The stream store (SPEC_SLICE4 section 6). Events from GET /runs/{id}/events are applied to the same
// run the polling writes, plus a display-only token overlay that is discarded once state settles.
import type { ArtifactKind, RunEvent, RunView } from "@procurement-agents/scope-api";
import { isProcurementView, scopeKey, TERMINAL_STATUSES } from "@procurement-agents/scope-api";

export interface StreamedText { node: string; text: string; done: boolean }
export interface SectionStream extends StreamedText { section_id: string; title: string | null }
/** The agent's reply typing itself into the chat drawer, with the tool it is using (SPEC_SLICE5). */
export interface ChatStream { text: string; done: boolean; activity: string | null; failed: string | null }
export interface Overlay { intake_turn: StreamedText | null; sections: Record<string, SectionStream>; chat: Record<string, ChatStream>; chatVersion: number }
export interface StreamStore { run: RunView | null; overlay: Overlay; seen: ReadonlySet<string>; lastId: string | null }

export const EMPTY_OVERLAY: Overlay = { intake_turn: null, sections: {}, chat: {}, chatVersion: 0 };
export const emptyStore = (run: RunView | null = null): StreamStore => ({ run, overlay: EMPTY_OVERLAY, seen: new Set(), lastId: null });

const DRAFTING_NODES = new Set(["draft_sow", "regenerate_section", "apply_fixes"]);

/** Apply one event. Idempotent: an event id already applied returns the store unchanged. Pure. */
export function applyEvent(store: StreamStore, ev: RunEvent): StreamStore {
  if (ev.id && store.seen.has(ev.id)) return store;
  const seen = new Set(store.seen); if (ev.id) seen.add(ev.id);
  const next: StreamStore = { ...store, seen, lastId: ev.id || store.lastId };
  const run = store.run;
  switch (ev.type) {
    case "status": {
      // Only "running" is applied from the wire. A terminal status carries no payload or permissions; the server
      // closes the stream right after it and the hook re-fetches state, which is the source of truth (spec 6).
      if (run && ev.data.status === "running" && run.status !== "running") next.run = { ...run, status: "running", next: null, ...("phase" in run ? { phase: ev.data.phase ?? run.phase, pending_interrupt: ev.data.pending_interrupt } : {}) } as RunView;
      return next;
    }
    case "node_started": {
      if (run && "current_node" in run) next.run = { ...run, status: "running", current_node: ev.data.node, current_started_at: ev.data.started_at, progress: null } as RunView;
      if (ev.data.node === "next_question" || ev.data.node === "record_answer" || ev.data.node === "intake_gate") next.overlay = { ...store.overlay, intake_turn: null };
      return next;
    }
    case "node_finished": {
      const o = store.overlay;
      const intake = o.intake_turn && o.intake_turn.node === ev.data.node ? { ...o.intake_turn, done: true } : o.intake_turn;
      const sections = DRAFTING_NODES.has(ev.data.node) ? Object.fromEntries(Object.entries(o.sections).map(([k, s]) => [k, s.node === ev.data.node ? { ...s, done: true } : s])) : o.sections;
      next.overlay = { ...store.overlay, intake_turn: intake, sections };
      return next;
    }
    case "progress": {
      if (run && "current_node" in run) next.run = { ...run, current_node: ev.data.node, progress: { done: ev.data.done, total: ev.data.total } } as RunView;
      return next;
    }
    case "token": {
      const { node, target, delta } = ev.data;
      const o = store.overlay;
      if (target.kind === "chat") {
        const key = scopeKey(target.scope);
        const cur = o.chat[key] ?? { text: "", done: false, activity: null, failed: null };
        next.overlay = { ...o, chat: { ...o.chat, [key]: { ...cur, text: cur.text + delta, done: false } } };
      } else if (target.kind === "intake_turn") {
        const cur = o.intake_turn && o.intake_turn.node === node && !o.intake_turn.done ? o.intake_turn : { node, text: "", done: false };
        next.overlay = { ...o, intake_turn: { ...cur, text: cur.text + delta } };
      } else if (target.kind === "section") {
        const cur = o.sections[target.section_id];
        const base: SectionStream = cur && cur.node === node && !cur.done ? cur : { section_id: target.section_id, title: target.title ?? cur?.title ?? null, node, text: "", done: false };
        next.overlay = { ...o, sections: { ...o.sections, [target.section_id]: { ...base, title: target.title ?? base.title, text: base.text + delta } } };
      }
      return next;
    }
    case "artifact": {
      if (run && isProcurementView(run)) next.run = { ...run, artifacts: { ...run.artifacts, [ev.data.kind as ArtifactKind]: { exists: true, updated_at: ev.data.updated_at } } };
      return next;
    }
    case "chat_turn": {
      const key = scopeKey(ev.data.scope);
      const o = store.overlay;
      if (ev.data.status === "started") next.overlay = { ...o, chat: { ...o.chat, [key]: { text: "", done: false, activity: null, failed: null } } };
      else next.overlay = { ...o, chat: { ...o.chat, [key]: { ...(o.chat[key] ?? { text: "" }), done: true, activity: null, failed: ev.data.status === "failed" ? ev.data.error ?? "The agent could not complete this turn." : null } }, chatVersion: o.chatVersion + 1 };
      if (run && ev.data.status !== "started") next.run = { ...run, agent_busy: false } as RunView;
      return next;
    }
    case "tool_call": {
      const key = scopeKey(ev.data.scope);
      const o = store.overlay;
      const cur = o.chat[key] ?? { text: "", done: false, activity: null, failed: null };
      next.overlay = { ...o, chat: { ...o.chat, [key]: { ...cur, activity: ev.data.name } } };
      return next;
    }
    case "proposal": {
      next.overlay = { ...store.overlay, chatVersion: store.overlay.chatVersion + 1 };
      return next;
    }
    case "recheck": {
      if (run && "rechecking" in run) {
        const cur = (run as { rechecking?: string[] }).rechecking ?? [];
        const rechecking = ev.data.status === "started" ? Array.from(new Set([...cur, ev.data.section_id])) : cur.filter((x) => x !== ev.data.section_id);
        next.run = { ...run, rechecking, agent_busy: ev.data.status === "started" ? true : (run as { agent_busy?: boolean }).agent_busy } as RunView;
      }
      if (ev.data.status !== "started") next.overlay = { ...store.overlay, chatVersion: store.overlay.chatVersion + 1 };
      return next;
    }
    case "suggestion": {
      if (run && "suggestions_pending" in run) next.run = { ...run, suggestions_pending: ((run as { suggestions_pending?: string[] }).suggestions_pending ?? []).filter((x) => x !== ev.data.section_id) } as RunView;
      return next;
    }
    default:
      return next;
  }
}

/** State from GET /runs/{id} replaces the run. Overlay text whose content has settled is discarded:
 *  everything once the run is no longer running, otherwise only targets whose node has finished. */
export function settle(store: StreamStore, run: RunView): StreamStore {
  const o = store.overlay;
  // chat text is discarded once its turn is done (the thread is re-fetched); the version counter survives
  const chat = Object.fromEntries(Object.entries(o.chat).filter(([, c]) => !c.done));
  if (TERMINAL_STATUSES.includes(run.status) || run.status === "idle") return { ...store, run, overlay: { ...EMPTY_OVERLAY, chat, chatVersion: o.chatVersion } };
  const sections = Object.fromEntries(Object.entries(o.sections).filter(([, s]) => !s.done));
  return { ...store, run, overlay: { intake_turn: o.intake_turn && !o.intake_turn.done ? o.intake_turn : null, sections, chat, chatVersion: o.chatVersion } };
}
